import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  LinearProgress,
  Divider,
  CircularProgress,
  IconButton,
} from "@mui/material";

import { omsService } from "../../utils/services/omsProvider";
import { tmsService } from "../../utils/services/tmsService";
import { wmsService } from "../../utils/services/wmsProvider";
import { clientService } from "../../utils/services/clientService"; // NEW
import { CloseOutlined } from "@mui/icons-material";

const ProgressItem = ({ label, value }) => {
  const isComplete = value === 100;
  const gradient = isComplete
    ? "linear-gradient(90deg, #30EF38 0%, #1BC235 100%)"
    : "linear-gradient(90deg, #9EC8F8 0%, #007DFF 100%)";

  return (
    <Box
      sx={{
        border: "1px solid var(--border-color)",
        borderRadius: 2,
        p: 2,
        boxShadow: 1,
        backgroundColor: "var(--bg-paper)",
      }}
    >
      {/* Top: Label + % */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography
          fontWeight={600}
          fontSize={14}
          color="var(--text-tertiary)"
          fontFamily="Poppins"
        >
          {label}
        </Typography>
        <Typography
          fontWeight={600}
          fontSize={14}
          color="var(--text-tertiary)"
          fontFamily="Poppins"
        >
          {value}%
        </Typography>
      </Box>

      <Box
        sx={{
          backgroundColor: "#F5F5F5",
          borderRadius: "50px",
          p: "3px",
        }}
      >
        <Box
          sx={{
            height: 10,
            width: `${value}%`,
            background: gradient,
            borderRadius: "50px",
            transition: "width 0.5s ease-in-out",
          }}
        />
      </Box>
    </Box>
  );
};

const ClientSetupStatusModal = ({ open, onClose, client }) => {
  const [progressData, setProgressData] = useState(null);
  const [loading, setLoading] = useState(false);

  const calculateWmsProgress = (data) => {
    if (!Array.isArray(data) || data.length === 0) return 0;
    const item = data[0];
    const totalFields = 8;
    let filled = 0;
    if (item?.provider) filled++;
    if (item?.consignor_name) filled++;
    if (item?.address1) filled++;
    if (item?.city) filled++;
    if (item?.province) filled++;
    if (item?.postal) filled++;
    if (item?.contact_number) filled++;
    if (item?.email) filled++;
    return Math.round((filled / totalFields) * 100);
  };

  const calculateTmsProgress = (data) => {
    if (!Array.isArray(data) || data.length === 0) return 0;
    const item = data[0];
    let score = 0;
    if (item?.provider) score += 50;
    if (Array.isArray(item?.authDetails) && item.authDetails.length > 0)
      score += 50;
    return score;
  };

  const calculateOmsProgress = (data) => {
    if (!Array.isArray(data) || data.length === 0) return 0;
    const scores = data.map((item) => {
      let filled = 0;
      if (item?.provider) filled++;
      if (item?.shop_name) filled++;
      if (item?.offline_token) filled++;
      return Math.round((filled / 3) * 100);
    });
    return Math.max(...scores);
  };

  const calculateAddressProgress = (address) => {
    if (!address) return 0;
    const fields = [
      "name",
      "address1",
      "city",
      "province",
      "postal",
      "country",
    ];
    const filled = fields.filter((key) => address[key]?.trim()).length;
    return Math.round((filled / fields.length) * 100);
  };

  const calculateLogoProgress = (logo) => {
    return logo ? 100 : 0;
  };

  const fetchSetupProgress = async (clientId) => {
    setLoading(true);
    const controller = new AbortController();
    const signal = controller.signal;

    try {
      const [omsResponse, tmsResponse, wmsResponse, clientDetails] =
        await Promise.all([
          omsService.fetch(clientId, "", "", { signal }),
          tmsService.fetch(clientId, "", "", { signal }),
          wmsService.fetch(clientId, "", "", { signal }),
          clientService.get(clientId),
        ]);

      const progress = {
        OMS: calculateOmsProgress(omsResponse.data),
        TMS: calculateTmsProgress(tmsResponse.data),
        "WMS + Consignor Details": calculateWmsProgress(wmsResponse.data),
        "Billing Address": calculateAddressProgress(
          clientDetails.billing_address,
        ),
        Logo: calculateLogoProgress(clientDetails.logo),
        "Return Address": calculateAddressProgress(
          clientDetails.return_address,
        ),
      };

      setProgressData(progress);
    } catch (error) {
      console.error("Fetch setup progress failed:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && client?._id) {
      fetchSetupProgress(client._id);
    }
  }, [open, client]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography fontWeight={600} fontSize={18}>
            {client?.business_name || client?.name}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseOutlined />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent dividers>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="200px"
          >
            <CircularProgress />
          </Box>
        ) : progressData ? (
          <Grid container spacing={2}>
            {Object.entries(progressData).map(([label, value]) => (
              <Grid item xs={12} sm={12} key={label}>
                <ProgressItem label={label} value={value} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography color="textSecondary" textAlign="center">
            No setup data found.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} color="secondary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClientSetupStatusModal;
